import BigNumber from 'bignumber.js';

import {
    CONCENTRATED_AMOUNT_INPUT_MAX_DECIMALS,
    CONCENTRATED_TICK_BASE,
    CONCENTRATED_TICK_LOG_BASE,
} from 'constants/amm';

import { formatBalance } from 'helpers/format-number';

export const clamp = (value: number, min: number, max: number) =>
    Math.min(max, Math.max(min, value));

export const snapDown = (value: number, spacing: number) => Math.floor(value / spacing) * spacing;

export const snapUp = (value: number, spacing: number) => Math.ceil(value / spacing) * spacing;

const PrecisePrice = BigNumber.clone({ DECIMAL_PLACES: 80 });

export const sqrtPriceX96ToPrice = (
    sqrtPriceX96: string | undefined,
    decimalsDiff: number,
): number | null => {
    if (!sqrtPriceX96 || !/^\d+$/.test(sqrtPriceX96) || !Number.isInteger(decimalsDiff))
        return null;
    const sqrt = new PrecisePrice(sqrtPriceX96);
    if (!sqrt.isPositive() || sqrt.isZero()) return null;
    const price = sqrt
        .pow(2)
        .times(new PrecisePrice(10).pow(decimalsDiff))
        .div(new PrecisePrice(2).pow(192))
        .toNumber();
    return Number.isFinite(price) && price > 0 ? price : null;
};

export const tickToPrice = (tick: number, decimalsDiff: number) =>
    Math.pow(CONCENTRATED_TICK_BASE, tick) * Math.pow(10, decimalsDiff);

export const priceToTick = (price: number, decimalsDiff: number) =>
    Math.log(price * Math.pow(10, -decimalsDiff)) / CONCENTRATED_TICK_LOG_BASE;

// Concentrated ticks encode sqrt(token1 / token0), so tickToPrice gives the price of
// token 0 in token 1. When token 1 is the displayed base the bounds are inverted,
// which also swaps lower and upper.
export const tickRangeToDisplayPriceRange = (
    tickLower: number,
    tickUpper: number,
    decimalsDiff: number,
    baseIndex: number,
): [number, number] => {
    const lower = tickToPrice(tickLower, decimalsDiff);
    const upper = tickToPrice(tickUpper, decimalsDiff);

    return baseIndex === 0 ? [lower, upper] : [1 / upper, 1 / lower];
};

// Inverse of tickRangeToDisplayPriceRange for a single price. The tick is fractional;
// callers snap it to the tick spacing.
export const displayPriceToTick = (price: number, decimalsDiff: number, baseIndex: number) =>
    priceToTick(baseIndex === 0 ? price : 1 / price, decimalsDiff);

// Reciprocal prices reverse the order of the bounds as well as the tick direction.
export const reverseConcentratedTickRange = <
    T extends { tickLower: number | null; tickUpper: number | null },
>(
    range: T,
): T => ({
    ...range,
    tickLower: range.tickUpper === null ? null : -range.tickUpper,
    tickUpper: range.tickLower === null ? null : -range.tickLower,
});

export const parseConcentratedPriceInput = (value: string) => {
    const normalized = value.replaceAll(',', '').trim();
    if (normalized === '') {
        return null;
    }

    const parsed = new BigNumber(normalized);
    if (!parsed.isFinite() || parsed.lte(0)) {
        return null;
    }

    return parsed.toNumber();
};

export const formatConcentratedPriceInputValue = (value: number) => {
    if (!Number.isFinite(value) || value <= 0) {
        return '';
    }

    if (value >= 1_000_000_000 || value < 0.000001) {
        return value.toExponential(6);
    }

    return formatBalance(value, false, false);
};

export const parseConcentratedAmount = (value: string) => {
    const normalized = value.replaceAll(',', '').trim();
    if (normalized === '') {
        return null;
    }

    const parsed = new BigNumber(normalized);
    return parsed.isFinite() ? parsed : null;
};

export const isValidNonNegativeConcentratedAmount = (value: string) => {
    const parsed = parseConcentratedAmount(value);
    return parsed !== null && parsed.gte(0);
};

export const formatConcentratedAmountInputValue = (
    value: BigNumber.Value,
    decimals: number,
    precision: number = decimals,
) => {
    const bnValue = new BigNumber(value);
    if (!bnValue.isFinite() || bnValue.lt(0)) {
        return '';
    }
    if (bnValue.isZero()) {
        return '0';
    }

    const fixed = bnValue.toFixed(Math.min(precision, decimals));
    const normalized = fixed.replace(/\.?0+$/, '');
    return normalized === '' ? '0' : normalized;
};

export const formatConcentratedDerivedAmount = (value: BigNumber.Value, decimals: number) =>
    formatConcentratedAmountInputValue(value, decimals, CONCENTRATED_AMOUNT_INPUT_MAX_DECIMALS);

export const parseConcentratedPercent = (value: string) => {
    const normalized = value.trim();
    if (normalized === '') {
        return null;
    }

    const percent = new BigNumber(normalized);
    return percent.isFinite() ? percent : null;
};
